import React, { useState } from 'react'
import { Layout, Menu, Button, Avatar, Dropdown, Badge, Space, Drawer, List, Typography } from 'antd'
import {
  MenuOutlined,
  UserOutlined,
  VideoCameraOutlined,
  PictureOutlined,
  HistoryOutlined,
  CreditCardOutlined,
  LogoutOutlined,
  SettingOutlined,
  BellOutlined,
  HomeOutlined,
  CrownOutlined,
  TeamOutlined
} from '@ant-design/icons'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { motion } from 'framer-motion'
import { on as onEvent } from '@/services/events'
import { useCreditsActivityStore } from '@/store/creditsActivityStore'
import { useSiteConfigStore } from '@/store/siteConfigStore'

const { Header: AntHeader } = Layout

const Header: React.FC = () => {
  // 添加自定义样式
  React.useEffect(() => {
    const style = document.createElement('style')
    style.textContent = `
      .navbar-center a:hover {
        background: #f5f5f5 !important;
        color: #000 !important;
      }
      .navbar-left a:hover {
        color: #0078d4 !important;
      }
    `
    document.head.appendChild(style)
    return () => {
      document.head.removeChild(style)
    }
  }, [])
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false)
  const user = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const logout = useAuthStore((s) => s.logout)
  const { activities, unread, clearUnread, loading } = useCreditsActivityStore()
  const { config: siteConfig, load: loadSiteConfig } = useSiteConfigStore()
  const location = useLocation()
  const navigate = useNavigate()
  
  // 判断是否显示左侧菜单栏（与 App.tsx 中的 showSidebar 逻辑一致）
  const hasSidebar = isAuthenticated && 
    !['/login', '/register', '/'].includes(location.pathname)
  
  // 顶部状态栏显示SSE连接状态（仅DEV）
  const [sseOk, setSseOk] = React.useState(true)
  React.useEffect(() => {
    onEvent('sse_status', (p) => setSseOk(!!p?.connected))
    
    // 监听配置更新，自动刷新Logo和站点名称
    onEvent('config_updated', (data: any) => {
      if (data.type === 'site_config' || data.type === 'system_config') {
        console.log('🔄 [Header] 检测到配置更新，重新加载...')
        loadSiteConfig()
      }
    })

    // 监听积分更新事件，实时更新用户积分余额
    onEvent('credits_updated', async (data: any) => {
      if (data.userId === user?.id) {
        console.log('🔄 [Header] 检测到积分更新，刷新用户信息', data)
        // 🔥 修复：调用 refreshUser() 获取最新完整用户信息，确保所有字段都更新
        try {
          await useAuthStore.getState().refreshUser()
          console.log('✅ [Header] 用户信息已刷新')
        } catch (error) {
          console.warn('⚠️ [Header] 刷新用户信息失败，使用事件数据更新:', error)
          // 降级方案：直接更新积分
          useAuthStore.getState().updateUser({ pointsBalance: data.pointsBalance })
        }
      }
    })
  }, [loadSiteConfig, user?.id])

  // 导航菜单项 - 根据是否有左侧菜单栏动态调整文字
  const menuItems = React.useMemo(() => [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: <Link to="/" className="navbar-center" style={{ fontSize: '14px', color: '#555', textDecoration: 'none', padding: '6px 10px', borderRadius: '4px', transition: 'background 0.2s' }}>首页</Link>,
    },
    {
      key: '/video',
      icon: <VideoCameraOutlined />,
      label: <Link to="/video" className="navbar-center" style={{ fontSize: '14px', color: '#555', textDecoration: 'none', padding: '6px 10px', borderRadius: '4px', transition: 'background 0.2s' }}>视频生成</Link>,
    },
    {
      key: '/image',
      icon: <PictureOutlined />,
      label: <Link to="/image" className="navbar-center" style={{ fontSize: '14px', color: '#555', textDecoration: 'none', padding: '6px 10px', borderRadius: '4px', transition: 'background 0.2s' }}>图像生成</Link>,
    },
    {
      key: '/history',
      icon: <HistoryOutlined />,
      label: <Link to="/history" className="navbar-center" style={{ fontSize: '14px', color: '#555', textDecoration: 'none', padding: '6px 10px', borderRadius: '4px', transition: 'background 0.2s' }}>{hasSidebar ? '历史' : '历史记录'}</Link>,
    },
    {
      key: '/payment',
      icon: <CreditCardOutlined />,
      label: <Link to="/payment" className="navbar-center" style={{ fontSize: '14px', color: '#555', textDecoration: 'none', padding: '6px 10px', borderRadius: '4px', transition: 'background 0.2s' }}>{hasSidebar ? '充值' : '积分充值'}</Link>,
    },
  ], [hasSidebar])

  // 用户菜单
  const userMenuItems = [
    ...(user?.role === 'ADMIN' ? [{
      key: 'admin',
      icon: <CrownOutlined />,
      label: '👑 管理后台',
      onClick: () => navigate('/admin'),
      style: {
        backgroundColor: '#fef3c7',
        color: '#f59e0b',
        fontWeight: 'bold'
      },
    }, {
      type: 'divider' as const,
    }] : []),
    ...(user?.role === 'AGENT_ADMIN' ? [{
      key: 'agent-portal',
      icon: <TeamOutlined />,
      label: '代理商门户',
      onClick: () => navigate('/agent-portal'),
      style: {
        backgroundColor: '#dbeafe',
        color: '#1d4ed8',
        fontWeight: 'bold'
      },
    }, {
      type: 'divider' as const,
    }] : []),
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人资料',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'history',
      icon: <HistoryOutlined />,
      label: '生成历史',
      onClick: () => navigate('/history'),
    },
    {
      key: 'payment',
      icon: <CreditCardOutlined />,
      label: '积分充值',
      onClick: () => navigate('/payment'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '设置',
      onClick: () => navigate('/profile'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: logout,
    },
  ]

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      logout()
    }
  }

  return (
    <AntHeader className="bg-white border-b border-gray-200 px-3 md:px-4 hidden md:block sticky top-0 z-50" style={{ height: '56px', fontFamily: 'Segoe UI, sans-serif' }}>
      <div className="flex items-center justify-between container-max max-w-7xl mx-auto h-full">
        {/* Logo */}
        <motion.div 
          className="flex items-center"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          style={{ gap: '12px' }}
        >
          <Link to="/" className="navbar-left flex items-center transition-colors duration-200" style={{ gap: '12px', fontWeight: '600', fontSize: '15px', color: '#333', textDecoration: 'none' }}>
            {siteConfig?.siteLogo ? (
              <img 
                src={siteConfig.siteLogo} 
                alt={siteConfig.siteName || 'Logo'} 
                className="h-8 object-contain"
              />
            ) : (
              <>
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <VideoCameraOutlined className="text-white text-lg" />
                </div>
                <span className="font-semibold hidden sm:block">
                  {siteConfig?.siteName || 'Sora2 AI'}
                </span>
              </>
            )}
          </Link>
        </motion.div>

        {/* 桌面端导航菜单 - 所有用户可见 */}
        <div className="hidden md:flex items-center" style={{ gap: hasSidebar ? '12px' : '20px' }}>
          <Menu
            mode="horizontal"
            selectedKeys={[location.pathname]}
            items={menuItems}
            className="border-none bg-transparent"
            style={{
              backgroundColor: 'transparent',
              border: 'none'
            }}
          />
        </div>

        {/* 右侧操作区 */}
        <div className="flex items-center" style={{ gap: '16px' }}>
          {isAuthenticated ? (
            <>
              {/* 积分显示 */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div 
                  className="hidden sm:flex items-center cursor-pointer hover:text-blue-600 transition-colors duration-200"
                  onClick={() => navigate('/payment')}
                  style={{ fontWeight: '600', color: '#0078d4', fontSize: '14px' }}
                >
                  <CreditCardOutlined style={{ marginRight: '4px' }} />
                  {user?.pointsBalance || 0} 积分
                </div>
              </motion.div>

              {/* 通知 */}
              <Dropdown
                placement="bottomRight"
                trigger={["click"]}
                menu={{ items: [] }}
                popupRender={() => (
                  <div className="w-96 p-0 rounded-lg shadow-xl bg-white border border-gray-200">
                    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                      <div className="text-sm font-semibold text-gray-700">积分通知</div>
                      <div className="text-xs text-gray-500">实时显示积分充值与消耗</div>
                    </div>
                    <List
                      className="max-h-80 overflow-auto"
                      itemLayout="horizontal"
                      dataSource={activities.slice(0, 20)}
                      locale={{ emptyText: loading ? '加载中...' : '暂无积分变化' }}
                      loading={loading}
                      renderItem={(a) => {
                        const isPlus = (a.delta || 0) >= 0
                        return (
                          <List.Item className="px-4">
                            <List.Item.Meta
                              avatar={
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white shadow ${isPlus ? 'bg-green-500' : 'bg-red-500'}`}>
                                  {isPlus ? '+' : '-'}
                                </div>
                              }
                              title={
                                <div className="flex items-center justify-between">
                                  <span className={`font-medium ${isPlus ? 'text-green-600' : 'text-red-600'}`}>
                                    {isPlus ? '+' : ''}{Math.abs(a.delta || 0)} 积分
                                  </span>
                                  <span className="text-gray-400 text-xs">
                                    {new Date(a.ts).toLocaleString('zh-CN')}
                                  </span>
                                </div>
                              }
                              description={
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-gray-500">
                                    {a.source === 'payment_recharge' ? '充值' :
                                     a.source === 'subscription_bonus' ? '订阅奖励' :
                                     a.source === 'test_recharge' ? '测试充值' :
                                     a.source === 'test_consume' ? '测试消耗' :
                                     a.source === 'image_generation' ? '图片生成' :
                                     a.source === 'video_generation' ? '视频生成' :
                                     a.source === 'register_bonus' ? '注册奖励' :
                                     (isPlus ? '充值/奖励' : '生成消耗')}
                                  </span>
                                  <span className="text-gray-500">余额：<span className="font-semibold text-gray-700">{a.balance || 0}</span></span>
                                </div>
                              }
                            />
                          </List.Item>
                        )
                      }}
                    />
                    <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-between items-center">
                      <Button size="small" onClick={() => clearUnread()}>
                        标记已读
                      </Button>
                      <Button type="link" size="small" onClick={() => navigate('/payment')}>
                        查看积分明细
                      </Button>
                    </div>
                  </div>
                )}
                onOpenChange={(open) => { if (open) clearUnread() }}
              >
                <Badge count={unread} overflowCount={99} dot={!sseOk && unread === 0} className="hidden sm:block" color="#ef4444">
                  <Button 
                    type="text" 
                    icon={<BellOutlined />}
                    className="text-gray-600 hover:text-blue-500 hover:bg-gray-100 rounded transition-all duration-200"
                    style={{ padding: '6px 10px' }}
                  />
                </Badge>
              </Dropdown>

              {/* 用户头像和菜单 */}
              <Dropdown
                menu={{ 
                  items: userMenuItems,
                  onClick: handleMenuClick
                }}
                trigger={['click']}
                placement="bottomRight"
              >
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="cursor-pointer"
                >
                  <div className="flex items-center hover:bg-gray-100 rounded transition-all duration-200" style={{ gap: '6px', fontSize: '14px', color: '#444', padding: '4px 8px' }}>
                    <Avatar 
                      src={user?.avatar} 
                      icon={<UserOutlined />}
                      className="bg-gradient-to-r from-blue-500 to-purple-600"
                      size="small"
                    />
                    <span className="hidden sm:block">
                      {user?.username || user?.email}
                    </span>
                  </div>
                </motion.div>
              </Dropdown>
            </>
          ) : (
            <div className="flex items-center" style={{ gap: '12px' }}>
              <Button 
                type="text" 
                onClick={() => navigate('/login')}
                className="text-gray-600 hover:text-blue-500 hover:bg-gray-100 rounded transition-all duration-200"
                style={{ padding: '6px 10px', fontSize: '14px' }}
              >
                登录
              </Button>
              <Button 
                type="primary" 
                onClick={() => navigate('/register')}
                className="bg-blue-600 border-none rounded transition-all duration-200"
                style={{ padding: '6px 12px', fontSize: '14px' }}
              >
                注册
              </Button>
            </div>
          )}

          {/* 移动端菜单按钮 */}
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setMobileMenuVisible(true)}
            className="md:hidden"
          />
        </div>
      </div>

      {/* 移动端抽屉菜单 */}
      <Drawer
        title={
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <VideoCameraOutlined className="text-white" />
            </div>
            <span className="text-lg font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              菜单
            </span>
          </div>
        }
        placement="right"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        width={320}
        className="md:hidden"
        styles={{
          body: {
            padding: '16px',
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
          }
        }}
      >
        <div className="space-y-6">
          {/* 移动端导航菜单 - 所有用户可见 */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-2 shadow-lg">
            <Menu
              mode="vertical"
              selectedKeys={[location.pathname]}
              items={menuItems}
              className="border-none bg-transparent rounded-xl"
              style={{
                backgroundColor: 'transparent',
                border: 'none'
              }}
            />
          </div>

          {isAuthenticated && (
            <>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
                <div className="flex items-center space-x-4 mb-6">
                  <Avatar 
                    src={user?.avatar} 
                    icon={<UserOutlined />}
                    className="bg-gradient-to-r from-blue-500 to-purple-600 shadow-md"
                    size="large"
                  />
                  <div>
                    <div className="font-semibold text-gray-900 text-lg">
                      {user?.username || user?.email}
                    </div>
                    <div className="text-sm text-gray-600 flex items-center space-x-1">
                      <CreditCardOutlined />
                      <span>积分: {user?.pointsBalance || 0}</span>
                    </div>
                  </div>
                </div>

                <Menu
                  mode="vertical"
                  items={userMenuItems}
                  className="border-none bg-transparent"
                  onClick={handleMenuClick}
                />
              </div>
            </>
          )}

          {!isAuthenticated && (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg space-y-3">
              <Button 
                block 
                onClick={() => {
                  navigate('/login')
                  setMobileMenuVisible(false)
                }}
                className="h-12 rounded-xl text-gray-700 hover:bg-gray-50 transition-all duration-200"
              >
                登录
              </Button>
              <Button 
                block 
                type="primary"
                onClick={() => {
                  navigate('/register')
                  setMobileMenuVisible(false)
                }}
                className="h-12 bg-gradient-to-r from-blue-500 to-purple-600 border-none rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
              >
                注册
              </Button>
            </div>
          )}
        </div>
      </Drawer>
    </AntHeader>
  )
}

export default Header
